import React, { useEffect, useRef } from 'react';
import Sigma from 'sigma';
import {
  buildGraph,
  applyLayout,
  createGraphDataHash,
  validateGraphData,
  createNodeReducer,
  getSigmaConfig,
  getLabelStyles,
  setupAllInteractions,
  cleanupFA2Layout,
  LockIconOverlay
} from './graph';

const GraphVisualization = ({ graphData, onNodeClick }) => {
  const containerRef = useRef(null);
  const sigmaRef = useRef(null);
  const layoutAppliedRef = useRef(false);
  const graphDataHashRef = useRef(null);
  const onNodeClickRef = useRef(onNodeClick);
  const lockOverlayRef = useRef(null);
  
  // Update the ref when the callback changes
  onNodeClickRef.current = onNodeClick;

  useEffect(() => {
    if (!containerRef.current) return;
    
    // Validate graph data
    if (!validateGraphData(graphData)) {
      console.warn('Invalid graph data provided');
      return;
    }

    // Create a hash of the graph data to detect actual changes
    const currentHash = createGraphDataHash(graphData);

    // Only reset layout if the actual graph data has changed
    if (graphDataHashRef.current !== currentHash) {
      layoutAppliedRef.current = false;
      graphDataHashRef.current = currentHash;
    }

    // Clean up any existing instances first
    if (lockOverlayRef.current) {
      lockOverlayRef.current.destroy();
      lockOverlayRef.current = null;
    }
    if (sigmaRef.current) {
      sigmaRef.current.kill();
      sigmaRef.current = null;
    }
    cleanupFA2Layout();

    // Build the graph with all nodes and edges
    const graph = buildGraph(graphData);

    let isCancelled = false;
    let cleanupInteractions = null;

    // Apply layout and handle async nature
    const setupVisualization = async () => {
      try {
        if (!layoutAppliedRef.current) {
          await applyLayout(graph);
          layoutAppliedRef.current = true;
        }

        // Check if effect was cancelled during async operation
        if (isCancelled) return;

        // Create Sigma instance with configuration
        const sigma = new Sigma(graph, containerRef.current, getSigmaConfig());

        // Store sigma instance
        sigmaRef.current = sigma;

        // Set up node reducer for zoom-based label display
        sigma.setSetting('nodeReducer', createNodeReducer(graph, sigma));

        // Set up all interactions (click, hover, zoom)
        cleanupInteractions = setupAllInteractions(sigma, graph, onNodeClickRef.current);

        // Set up lock icon overlay for private nodes
        lockOverlayRef.current = new LockIconOverlay(sigma, containerRef.current);

      } catch (error) {
        console.error('Failed to setup graph visualization:', error);
      }
    };
    
    // Setup visualization
    setupVisualization();

    // Cleanup function
    return () => {
      isCancelled = true;
      
      if (cleanupInteractions) {
        cleanupInteractions();
        cleanupInteractions = null;
      }
      
      if (lockOverlayRef.current) {
        lockOverlayRef.current.destroy();
        lockOverlayRef.current = null;
      }
      
      if (sigmaRef.current) {
        sigmaRef.current.kill();
        sigmaRef.current = null;
      }
      
      // Clean up Force Atlas 2 layout
      cleanupFA2Layout();
      
      // Reset layout flag when component unmounts
      layoutAppliedRef.current = false;
    };
  }, [graphData]);





  return (
    <div 
      ref={containerRef} 
      style={{ 
        width: '100%', 
        height: '100vh',
        position: 'absolute',
        top: 0,
        left: 0,
        zIndex: 1
      }}
    >
      <style>{getLabelStyles()}</style>
    </div>
  );
};

export default GraphVisualization;
